import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { levelDisplayNames } from "../../utils/displayNameUtils";
import { categoryLabels, subcategoryLabels, subcategoryLabelsMap } from "../../utils/categoryLabels";
import { getRiskLevelColor } from "../../utils/riskLevelUtils";
import StatusResultModal from "../StatusResultModal";

function PromptsResultModal({ modal, selectedCategory, setSelectedCategory, onClose, keyPrefix = "" }) {
  const { t } = useTranslation("generate");

  const relevantSubcategories = useMemo(() => {
    if (!selectedCategory) return null;
    const fromMapping = subcategoryLabels?.[selectedCategory];
    if (!fromMapping || typeof fromMapping !== "object") return [];

    const available = modal?.data?.subcategories;
    if (!available || typeof available !== "object" || Array.isArray(available)) return [];

    const allowedKeys = new Set(Object.keys(fromMapping));
    return Object.entries(available).filter(([key]) => allowedKeys.has(String(key)));
  }, [modal?.data?.subcategories, selectedCategory]);

  return (
    <StatusResultModal
      open={Boolean(modal?.open)}
      type={modal?.type}
      message={modal?.message}
      onClose={onClose}
    >
      {modal?.data != null && (
        typeof modal.data === "object" &&
        !Array.isArray(modal.data) ? (
          <div className="generate-modal-sections">
            <section className="generate-modal-subsection">
              <div className="generate-modal-subsection-title">{t("modal.summary")}</div>
              <div className="generate-modal-summary-grid">
                <div className="generate-modal-summary-card">
                  <div className="generate-modal-summary-count">
                    {modal.data.total_prompts ?? "—"}
                  </div>
                  <div className="generate-modal-summary-title">{t("modal.totalPromptsSaved")}</div>
                </div>
                <div className="generate-modal-summary-card">
                  <div className="generate-modal-summary-count">
                    {(() => {
                      const raw = modal.data.avg_prompt_length;
                      const num = typeof raw === "number" ? raw : Number.parseFloat(String(raw));
                      return Number.isFinite(num) ? num.toFixed(2) : "—";
                    })()}
                  </div>
                  <div className="generate-modal-summary-title">{t("modal.avgPromptLength")}</div>
                </div>
                <div className="generate-modal-summary-card">
                  <div className="generate-modal-summary-count">
                    {modal.data.unique_prompts_generated ?? "—"}
                  </div>
                  <div className="generate-modal-summary-title">{t("modal.uniquePromptsCreated")}</div>
                </div>
              </div>
            </section>
            {modal.data.severity &&
              typeof modal.data.severity === "object" &&
              !Array.isArray(modal.data.severity) && (
                <section className="generate-modal-subsection">
                  <div className="generate-modal-subsection-title">{t("modal.promptsBySeverity")}</div>
                  <div className="generate-modal-chips" role="list">
                    {Object.entries(modal.data.severity).map(([key, value]) => {
                      const severityKey = String(key);
                      const color = getRiskLevelColor(severityKey);
                      const pillBg =
                        typeof color === "string" && color.startsWith("#") && color.length === 7
                          ? `${color}22`
                          : "rgba(59, 130, 246, 0.18)";
                      const pillBorder =
                        typeof color === "string" && color.startsWith("#") && color.length === 7
                          ? `${color}55`
                          : "rgba(59, 130, 246, 0.25)";

                      return (
                        <div
                          key={`${keyPrefix}severity-${severityKey}`}
                          className="generate-modal-chip generate-modal-chip--severity"
                          role="listitem"
                          style={{ borderColor: pillBorder }}
                        >
                          <span className="generate-modal-chip-label">
                            {levelDisplayNames[severityKey] ?? severityKey}
                          </span>
                          <span
                            className="generate-modal-chip-count"
                            style={{
                              backgroundColor: pillBg,
                              borderColor: pillBorder,
                              color,
                            }}
                          >
                            {value ?? "—"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
            {modal.data.categories &&
              typeof modal.data.categories === "object" &&
              !Array.isArray(modal.data.categories) && (
                <section className="generate-modal-subsection">
                  <div className="generate-modal-subsection-title">{t("modal.promptsPerCategory")}</div>
                  <div className="generate-modal-chips" role="list">
                    {Object.entries(modal.data.categories).map(([key, value]) => (
                      <button
                        key={`${keyPrefix}category-${key}`}
                        type="button"
                        className={`generate-modal-chip generate-modal-chip--clickable ${selectedCategory === String(key) ? "is-active" : ""}`}
                        role="listitem"
                        aria-pressed={selectedCategory === String(key)}
                        onClick={() =>
                          setSelectedCategory((prev) => (prev === String(key) ? null : String(key)))
                        }
                      >
                        <span className="generate-modal-chip-label">
                          {categoryLabels[String(key)] ?? String(key)}
                        </span>
                        <span className="generate-modal-chip-count">{value ?? "—"}</span>
                      </button>
                    ))}
                  </div>
                </section>
              )}
            {modal.data.subcategories &&
              typeof modal.data.subcategories === "object" &&
              !Array.isArray(modal.data.subcategories) && (
                <section className="generate-modal-subsection generate-modal-subsection-no-scroll">
                  <div className="generate-modal-subsection-title">{t("modal.promptsPerSubcategory")}</div>
                  {!selectedCategory ? (
                    <div className="generate-modal-hint">{t("modal.selectCategoryHint")}</div>
                  ) : (
                    <div className="generate-modal-chips" role="list">
                      {(relevantSubcategories ?? []).map(([key, value]) => (
                        <div
                          key={`${keyPrefix}subcategory-${key}`}
                          className="generate-modal-chip"
                          role="listitem"
                        >
                          <span className="generate-modal-chip-label">
                            {subcategoryLabelsMap[String(key)] ?? String(key)}
                          </span>
                          <span className="generate-modal-chip-count">{value ?? "—"}</span>
                        </div>
                      ))}
                      {relevantSubcategories && relevantSubcategories.length === 0 && (
                        <div className="generate-modal-hint">{t("modal.noSubcategories")}</div>
                      )}
                    </div>
                  )}
                </section>
              )}
          </div>
        ) : (
          <pre className="generate-modal-api-data">
            {typeof modal.data === "string"
              ? modal.data
              : JSON.stringify(modal.data, null, 2)}
          </pre>
        )
      )}
    </StatusResultModal>
  );
}

export default PromptsResultModal;
